import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Building2, Users, CreditCard, Bell, Plug, Briefcase, Key, ChevronRight,
  Plus, DollarSign,
  GitBranch, LayoutGrid, CalendarDays, ToggleLeft, Upload as UploadIcon, ShieldCheck, Globe,
} from "lucide-react";
import { Form, Input, Select, Switch, Button, Modal, InputNumber, Spin, Skeleton, Table, Tag, Drawer, DatePicker, TimePicker, Popconfirm, Upload, message, Tooltip, Empty, Radio, Space, Divider, Typography, Card } from "antd";
import axiosInstance from "../../../lib/axios";
import type { UploadFile, UploadProps } from "antd";
import { useFileSave } from "../../../hooks/api/file/hooks";
import {
  useBranchList, useBranchCreate, useBranchUpdate, useBranchDelete,
  useDivisionList, useDivisionCreate, useDivisionUpdate, useDivisionDelete,
  useHolidayList, useHolidayCreate, useHolidayUpdate, useHolidayDelete,
  useFeatureOverrideList, useFeatureOverrideSet, useFeatureOverrideRemove,
} from "../../../hooks/api/company-config/hooks";
import { useIntegrations, useIntegrationConnectUrl, useDisconnectIntegration, useIntegrationHub, useConnectIntegration, useTestIntegration, useDisconnectHubIntegration, useXeroAuthUrl, useQBAuthUrl, useGCalAuthUrl } from "../../../hooks/api/integrations/hooks";
import { useCompanyBillingInvoices, useGetBillingInvoicePdf, useCancelAccount, useUpdatePlanType } from "../../../hooks/api/billing/hooks";
import { useNotificationPreferences, useUpdateNotificationPreferences } from "../../../hooks/api/notifications/hooks";
import { INotificationPreferences, IIntegrationCatalogItem } from "../../../types/api/integrations";
import { IBranch, IDivision, IHoliday, IFeatureOverride, THolidayType } from "../../../types/api/company-config";
import { useMessageApi } from "../../../provider/AntdNotification";
import { useGetMyCompany, useUpdateMyCompany } from "../../../hooks/api/company/hooks";
import { useUserStore } from "../../../store/slices/user";
import { useJobTypeList } from "../../../hooks/api/job-config/hooks";
import { IJobType } from "../../../types/api/job-config";
import dayjs from "dayjs";
import UiCard from "../../../components/ui/Card";

// ─── Static data ──────────────────────────────────────────────────────────────
const COMING_SOON_INTEGRATIONS = [
  { id: "google_calendar", name: "Google Calendar", desc: "Two-way schedule sync",           color: "bg-red-500" },
  { id: "ms365",           name: "Microsoft 365",   desc: "Outlook calendar & email",        color: "bg-blue-600" },
  { id: "slack",           name: "Slack",           desc: "Job alerts & team notifications", color: "bg-orange-500" },
  { id: "twilio",          name: "Twilio",          desc: "SMS notifications to clients",    color: "bg-red-600" },
  { id: "zapier",          name: "Zapier",          desc: "Workflow automation & triggers",  color: "bg-amber-500" },
  { id: "docusign",        name: "DocuSign",        desc: "E-signatures for contracts/quotes", color: "bg-amber-600" },
];

const REAL_INTEGRATION_META: Record<string, { name: string; desc: string; color: string }> = {
  xero:       { name: "Xero",       desc: "Accounting & invoicing sync", color: "bg-blue-500"  },
  quickbooks: { name: "QuickBooks", desc: "Bookkeeping & payroll",       color: "bg-green-500" },
  sage:       { name: "Sage",       desc: "Accounting & payroll suite",  color: "bg-emerald-600" },
  freeagent:  { name: "FreeAgent",  desc: "Small business accounting",   color: "bg-teal-500"  },
};

const TIMEZONE_OPTIONS = [
  { value: "Europe/London", label: "Europe/London (UTC+0/+1)" },
  { value: "Europe/Paris", label: "Europe/Paris (UTC+1/+2)" },
  { value: "America/New_York", label: "America/New_York (UTC-5/-4)" },
  { value: "America/Los_Angeles", label: "America/Los_Angeles (UTC-8/-7)" },
  { value: "Asia/Dubai", label: "Asia/Dubai (UTC+4)" },
  { value: "Asia/Kolkata", label: "Asia/Kolkata (UTC+5:30)" },
];

const DATE_FORMAT_OPTIONS = [
  { value: "DD/MM/YYYY", label: "DD/MM/YYYY (UK)" },
  { value: "MM/DD/YYYY", label: "MM/DD/YYYY (US)" },
  { value: "YYYY-MM-DD", label: "YYYY-MM-DD (ISO)" },
];

const CURRENCY_OPTIONS = [
  { value: "GBP", label: "GBP (£)" },
  { value: "EUR", label: "EUR (€)" },
  { value: "USD", label: "USD ($)" },
];

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  value: i + 1,
  label: dayjs().month(i).format("MMMM"),
}));

// ─── Sub-components ────────────────────────────────────────────────────────────
const SectionHeader = ({ title, sub, action }: { title: string; sub: string; action?: React.ReactNode }) => (
  <div className="flex items-center justify-between mb-5">
    <div>
      <h3 className="text-base font-semibold text-slate-800">{title}</h3>
      <p className="text-sm text-slate-400 mt-0.5">{sub}</p>
    </div>
    {action}
  </div>
);

const SettingsCard = ({ children }: { children: React.ReactNode }) => (
  <UiCard className="p-5">{children}</UiCard>
);

// ─── Company Profile Tab (wired to real API) ──────────────────────────────────
const CompanyTab = () => {
  const [form] = Form.useForm();
  const messageApi = useMessageApi();
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const { data: res, isLoading } = useGetMyCompany();
  const company = res?.data;

  const { mutate: update, isPending: saving } = useUpdateMyCompany({
    onSuccess: () => messageApi.success("Company profile saved."),
    onError: (err: any) => messageApi.error(err?.message || "Failed to save."),
  });

  const { mutate: uploadFile } = useFileSave({
    onSuccess: (res: any) => {
      const url = res?.data?.url ?? res?.url ?? null;
      if (url) {
        setLogoUrl(url);
        form.setFieldValue("logo_url", url);
        messageApi.success("Logo uploaded.");
      }
      setUploading(false);
    },
    onError: (err: any) => {
      messageApi.error(err?.message || "Logo upload failed.");
      setUploading(false);
    },
  });

  useEffect(() => {
    if (!company) return;
    setLogoUrl(company.logo_url ?? null);
    form.setFieldsValue({
      name: company.name,
      email: company.email,
      phone: company.phone,
      website: company.website,
      vat_number: company.vat_number,
      vat_registered: company.vat_registered,
      company_registration_number: company.company_registration_number,
      address_line1: company.address_line1,
      address_line2: company.address_line2,
      city: company.city,
      county: company.county,
      postcode: company.postcode,
      country: company.country,
      timezone: company.timezone,
      currency: company.currency,
      logo_url: company.logo_url,
    });
  }, [company, form]);

  const LOGO_MAX_BYTES = 2 * 1024 * 1024; // 2MB — matches the hint text below the upload button
  const LOGO_ALLOWED_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/svg+xml", "image/webp"];

  // The UI has always claimed "PNG, JPG or SVG. Max 2MB." but nothing actually
  // enforced it — `accept="image/*"` on <Upload> is just a file-picker filter
  // hint and does nothing to stop a drag-and-drop or a renamed file. Validate
  // for real before ever calling the upload API.
  const validateLogoFile = (file: File): string | null => {
    if (!LOGO_ALLOWED_TYPES.includes(file.type)) {
      return "Only PNG, JPG, WEBP or SVG images are allowed.";
    }
    if (file.size > LOGO_MAX_BYTES) {
      return "Logo must be 2MB or smaller.";
    }
    return null;
  };

  const handleLogoBeforeUpload: UploadProps["beforeUpload"] = (file) => {
    const error = validateLogoFile(file as File);
    if (error) {
      messageApi.error(error);
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const handleLogoUpload: UploadProps["customRequest"] = ({ file }) => {
    const error = validateLogoFile(file as File);
    if (error) {
      // Defense in depth in case customRequest is ever reached without
      // beforeUpload running (e.g. a future drag-and-drop path).
      messageApi.error(error);
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file as File);
    uploadFile(fd);
  };

  const onSave = () => {
    form.validateFields().then((values) => update({ ...values, logo_url: logoUrl ?? undefined }));
  };

  if (isLoading) return <SettingsCard><Skeleton active paragraph={{ rows: 8 }} /></SettingsCard>;

  return (
    <SettingsCard>
      <SectionHeader title="Company Profile" sub="Business details used across quotes, invoices and reports" />
      <Form form={form} layout="vertical" size="middle">
        {/* Logo */}
        <div className="mb-5">
          <label className="block text-sm font-medium text-slate-700 mb-2">Company Logo</label>
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-xl border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden flex-shrink-0">
              {logoUrl ? (
                <img src={logoUrl} alt="Logo" className="w-full h-full object-contain p-1" />
              ) : (
                <Building2 size={28} className="text-slate-300" />
              )}
            </div>
            <div>
              <Upload
                accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                showUploadList={false}
                beforeUpload={handleLogoBeforeUpload}
                customRequest={handleLogoUpload}
              >
                <Button
                  icon={<UploadIcon size={14} />}
                  loading={uploading}
                  className="flex items-center gap-1.5"
                >
                  {logoUrl ? "Change Logo" : "Upload Logo"}
                </Button>
              </Upload>
              {logoUrl && (
                <button
                  type="button"
                  onClick={() => { setLogoUrl(null); form.setFieldValue("logo_url", null); }}
                  className="mt-1.5 text-xs text-red-500 hover:text-red-700 block"
                >
                  Remove logo
                </button>
              )}
              <p className="text-xs text-slate-400 mt-1">PNG, JPG or SVG. Max 2MB.</p>
            </div>
          </div>
          <Form.Item name="logo_url" hidden><Input /></Form.Item>
        </div>

        <div className="grid grid-cols-2 gap-x-5">
          <Form.Item name="name" label="Registered Company Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: "email" }]}><Input /></Form.Item>
        </div>
        <div className="grid grid-cols-2 gap-x-5">
          <Form.Item name="phone" label="Phone"><Input /></Form.Item>
          <Form.Item name="website" label="Website"><Input /></Form.Item>
        </div>
        <div className="grid grid-cols-2 gap-x-5">
          <Form.Item name="vat_number" label="VAT Number"><Input /></Form.Item>
          <Form.Item name="company_registration_number" label="Companies House Number"><Input /></Form.Item>
        </div>
        <Form.Item name="vat_registered" label="VAT Registered" valuePropName="checked">
          <Switch />
        </Form.Item>
        <div className="grid grid-cols-2 gap-x-5">
          <Form.Item name="address_line1" label="Address Line 1"><Input /></Form.Item>
          <Form.Item name="address_line2" label="Address Line 2"><Input /></Form.Item>
        </div>
        <div className="grid grid-cols-3 gap-x-5">
          <Form.Item name="city" label="City"><Input /></Form.Item>
          <Form.Item name="county" label="County"><Input /></Form.Item>
          <Form.Item name="postcode" label="Postcode"><Input /></Form.Item>
        </div>
        <div className="grid grid-cols-3 gap-x-5">
          <Form.Item name="country" label="Country"><Input /></Form.Item>
          <Form.Item name="timezone" label="Timezone">
            <Select options={TIMEZONE_OPTIONS} />
          </Form.Item>
          <Form.Item name="currency" label="Currency">
            <Select options={CURRENCY_OPTIONS} />
          </Form.Item>
        </div>
        <Button type="primary" className="mt-2" onClick={onSave} loading={saving}>
          Save Changes
        </Button>
      </Form>
    </SettingsCard>
  );
};

// ─── Financial Settings Tab (wired to real API) ───────────────────────────────
const FinancialTab = () => {
  const [form] = Form.useForm();
  const messageApi = useMessageApi();
  const { data: res, isLoading } = useGetMyCompany();
  const company = res?.data;

  const { mutate: update, isPending: saving } = useUpdateMyCompany({
    onSuccess: () => messageApi.success("Financial settings saved."),
    onError: (err: any) => messageApi.error(err?.message || "Failed to save."),
  });

  useEffect(() => {
    if (!company) return;
    form.setFieldsValue({
      default_vat_rate: company.default_vat_rate,
      default_payment_terms_days: company.default_payment_terms_days,
      financial_year_start_month: company.financial_year_start_month,
      date_format: company.date_format,
      invoice_prefix: company.invoice_prefix,
      invoice_number_format: company.invoice_number_format,
      quote_prefix: company.quote_prefix,
      quote_number_format: company.quote_number_format,
      job_number_format: company.job_number_format,
      po_number_format: (company as any).po_number_format,
      working_hours_start: company.working_hours_start ? dayjs(company.working_hours_start, "HH:mm") : null,
      working_hours_end: company.working_hours_end ? dayjs(company.working_hours_end, "HH:mm") : null,
      buffer_time_minutes: (company as any).buffer_time_minutes ?? 0,
    });
  }, [company, form]);

  const onSave = () => {
    form.validateFields().then((values) => {
      update({
        ...values,
        working_hours_start: values.working_hours_start ? values.working_hours_start.format("HH:mm") : null,
        working_hours_end: values.working_hours_end ? values.working_hours_end.format("HH:mm") : null,
        buffer_time_minutes: values.buffer_time_minutes ?? 0,
      });
    });
  };

  if (isLoading) return <div className="space-y-5"><SettingsCard><Skeleton active paragraph={{ rows: 6 }} /></SettingsCard></div>;

  return (
    <div className="space-y-5">
      <SettingsCard>
        <SectionHeader title="Tax & Payments" sub="VAT and payment defaults applied to new invoices and quotes" />
        <Form form={form} layout="vertical" size="middle">
          <div className="grid grid-cols-2 gap-x-5">
            <Form.Item name="default_vat_rate" label="Default VAT Rate (%)">
              <InputNumber min={0} max={100} step={0.5} className="w-full" suffix="%" />
            </Form.Item>
            <Form.Item name="default_payment_terms_days" label="Default Payment Terms (days)">
              <InputNumber min={0} max={365} className="w-full" suffix="days" />
            </Form.Item>
          </div>
        </Form>
      </SettingsCard>

      <SettingsCard>
        <SectionHeader title="Numbering Formats" sub="Tokens: {YEAR} {MONTH} {SEQ} — e.g. INV-{YEAR}-{SEQ}" />
        <Form form={form} layout="vertical" size="middle">
          <div className="grid grid-cols-2 gap-x-5">
            <Form.Item name="invoice_prefix" label="Invoice Prefix"><Input placeholder="INV" /></Form.Item>
            <Form.Item name="invoice_number_format" label="Invoice Format"><Input placeholder="INV-{YEAR}-{SEQ}" /></Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-x-5">
            <Form.Item name="quote_prefix" label="Quote Prefix"><Input placeholder="QT" /></Form.Item>
            <Form.Item name="quote_number_format" label="Quote Format"><Input placeholder="QT-{YEAR}-{SEQ}" /></Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-x-5">
            <Form.Item name="job_number_format" label="Job Number Format"><Input placeholder="JOB-{YEAR}-{SEQ}" /></Form.Item>
            <Form.Item name="po_number_format" label="Purchase Order Format"><Input placeholder="PO-{YEAR}-{SEQ}" /></Form.Item>
          </div>
        </Form>
      </SettingsCard>

      <SettingsCard>
        <SectionHeader title="Regional & Working Hours" sub="Localisation and business hours" />
        <Form form={form} layout="vertical" size="middle">
          <div className="grid grid-cols-2 gap-x-5">
            <Form.Item name="date_format" label="Date Format">
              <Select options={DATE_FORMAT_OPTIONS} />
            </Form.Item>
            <Form.Item name="financial_year_start_month" label="Financial Year Start Month">
              <Select options={MONTH_OPTIONS} />
            </Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-x-5">
            <Form.Item name="working_hours_start" label="Working Hours Start">
              <TimePicker format="HH:mm" minuteStep={15} className="w-full" placeholder="08:00" />
            </Form.Item>
            <Form.Item name="working_hours_end" label="Working Hours End">
              <TimePicker format="HH:mm" minuteStep={15} className="w-full" placeholder="18:00" />
            </Form.Item>
          </div>
          <Form.Item name="buffer_time_minutes" label="Buffer Time Between Jobs" extra="Minimum gap required between jobs for the same engineer">
            <InputNumber min={0} max={120} step={15} addonAfter="minutes" className="w-40" />
          </Form.Item>
          <Button type="primary" className="mt-2" onClick={onSave} loading={saving}>
            Save Financial Settings
          </Button>
        </Form>
      </SettingsCard>
    </div>
  );
};

// ─── Users Tab ────────────────────────────────────────────────────────────────
const UsersTab = () => {
  const { data: res } = useGetMyCompany();
  const company = res?.data;
  const navigate = useNavigate();

  return (
    <SettingsCard>
      <SectionHeader
        title="Users & Roles"
        sub={company ? `Users on ${company.name}` : "Users on your account"}
      />
      <div className="flex flex-col items-center py-10 gap-4">
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={null} />
        <p className="text-sm text-slate-500 text-center max-w-sm">
          Full user role management is coming soon. Manage field workers and their access from the Field Workers page.
        </p>
        <Button type="primary" onClick={() => navigate("/field-workers")}>
          Manage Field Workers
        </Button>
      </div>
    </SettingsCard>
  );
};

// ─── Billing Tab ──────────────────────────────────────────────────────────────
const BillingTab = () => {
  const messageApi = useMessageApi();
  const { data: res } = useGetMyCompany();
  const company = res?.data as any;
  const sub = company?.subscription;

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelDetail, setCancelDetail] = useState("");

  const { data: invoicesRes, isLoading: invoicesLoading, isError: invoicesError } = useCompanyBillingInvoices();
  const invoices = invoicesRes?.data ?? [];

  const { mutate: fetchPdf, isPending: pdfLoading } = useGetBillingInvoicePdf({
    onSuccess: (res: any) => {
      const pdfUrl = res?.data?.pdf_url ?? res?.pdf_url;
      if (pdfUrl) {
        window.open(pdfUrl, "_blank");
      } else {
        messageApi.info("Invoice PDF not available yet.");
      }
    },
    onError: () => messageApi.info("Invoice PDF not available yet."),
  });

  const { mutate: updatePlanType, isPending: updatingPlan } = useUpdatePlanType();
  const { mutate: cancelAccount, isPending: cancelling } = useCancelAccount();

  const currentPlanType: "monthly" | "annual" = company?.plan_type || "monthly";

  const handleDownloadPdf = (id: number) => {
    fetchPdf(id);
  };

  const handleSwitchPlan = (plan: "monthly" | "annual") => {
    if (plan === currentPlanType) return;
    Modal.confirm({
      title: plan === "annual" ? "Switch to Annual billing?" : "Switch to Monthly billing?",
      content: plan === "annual"
        ? "Annual billing saves you 20%. You will be billed annually."
        : "Switch back to monthly billing.",
      onOk: () => {
        updatePlanType(plan, {
          onSuccess: () => messageApi.success(`Switched to ${plan} billing.`),
          onError: () => messageApi.error("Failed to update plan type."),
        });
      },
    });
  };

  const invoiceColumns = [
    {
      title: "Invoice #",
      dataIndex: "invoice_number",
      key: "invoice_number",
      render: (v: string) => <span className="font-mono text-xs text-slate-700">{v}</span>,
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      render: (v: string) => <span className="text-sm text-slate-600">{dayjs(v).format("DD MMM YYYY")}</span>,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (v: string | number, r: any) => (
        <span className="text-sm font-medium text-slate-800">
          {r.currency === "GBP" ? "£" : r.currency === "USD" ? "$" : "€"}{Number(v).toFixed(2)}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v: string) => (
        <Tag color={v === "paid" ? "green" : v === "open" ? "orange" : "default"}>
          {v.charAt(0).toUpperCase() + v.slice(1)}
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_: any, r: any) => (
        <Button
          size="small"
          loading={pdfLoading}
          onClick={() => handleDownloadPdf(r.id)}
          icon={<DollarSign size={12} />}
        >
          Download PDF
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <SettingsCard>
        <SectionHeader title="Current Plan" sub={sub ? `${sub.plan_name ?? "Plan"} — ${sub.status}` : "No active subscription"} />
        {sub ? (
          <div className="flex items-start gap-6">
            <div className="flex-1 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl p-5 text-white">
              <div className="text-xs font-semibold uppercase tracking-widest opacity-70 mb-1">{sub.plan_name}</div>
              <div className="text-3xl font-bold mb-0.5">
                {sub.amount != null ? `£${Number(sub.amount).toFixed(0)}` : "Custom"}<span className="text-base font-normal opacity-70">/month</span>
              </div>
              {sub.ends_at && (
                <div className="text-xs opacity-60 mb-2">Renews {dayjs(sub.ends_at).format("DD MMM YYYY")}</div>
              )}
              <div className="flex items-center gap-2 mt-2">
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                  sub.status === "active" ? "bg-emerald-500" :
                  sub.status === "trial" ? "bg-amber-500" : "bg-red-500"
                } text-white`}>{sub.status.toUpperCase()}</span>
              </div>
            </div>
            <div className="w-48 space-y-3">
              <button
                className="w-full border border-slate-200 bg-white text-slate-600 text-sm font-medium py-2 rounded-lg hover:bg-slate-50 transition-colors"
                onClick={() => window.open("mailto:support@arruk.co.uk?subject=Upgrade Plan", "_blank")}
              >
                Upgrade Plan
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-slate-400 text-sm">No subscription data available.</div>
        )}
      </SettingsCard>

      {/* Billing Cycle Toggle */}
      <SettingsCard>
        <SectionHeader title="Billing Cycle" sub="Choose between monthly or annual billing" />
        <Space>
          <Button
            type={currentPlanType === "monthly" ? "primary" : "default"}
            className={currentPlanType === "monthly" ? "" : ""}
            loading={updatingPlan && currentPlanType !== "monthly"}
            onClick={() => handleSwitchPlan("monthly")}
          >
            Monthly
          </Button>
          <Button
            type={currentPlanType === "annual" ? "primary" : "default"}
            className={currentPlanType === "annual" ? "" : ""}
            loading={updatingPlan && currentPlanType !== "annual"}
            onClick={() => handleSwitchPlan("annual")}
          >
            Annual (Save 20%)
          </Button>
        </Space>
        <Typography.Text type="secondary" style={{ display: "block", marginTop: 8 }}>
          Current: {currentPlanType === "annual" ? "Annual (20% discount applied)" : "Monthly"}
        </Typography.Text>
      </SettingsCard>

      <SettingsCard>
        <SectionHeader title="Billing History" sub="Download PDF copies of your invoices" />
        {invoicesLoading ? (
          <div className="flex justify-center py-8"><Spin /></div>
        ) : invoicesError || invoices.length === 0 ? (
          <div className="text-center py-8 text-slate-400 text-sm">No billing history available.</div>
        ) : (
          <Table
            dataSource={invoices}
            columns={invoiceColumns}
            rowKey="id"
            size="small"
            pagination={false}
          />
        )}
      </SettingsCard>

      {/* Cancel Account */}
      <SettingsCard>
        <Divider style={{ marginTop: 0 }} />
        <SectionHeader
          title="Cancel Account"
          sub="Stop all services at the end of your current billing period. Data retained for 30 days."
        />
        {company?.cancelled_at ? (
          <Tag color="red">Cancellation scheduled — access until end of billing period.</Tag>
        ) : (
          <Button danger onClick={() => setShowCancelModal(true)}>
            Cancel Account
          </Button>
        )}
      </SettingsCard>

      {/* Cancel Modal */}
      <Modal
        open={showCancelModal}
        title="Cancel Your Account"
        onCancel={() => { setShowCancelModal(false); setCancelReason(""); setCancelDetail(""); }}
        footer={null}
        width={480}
      >
        <Typography.Paragraph>We are sorry to see you go. Please tell us why:</Typography.Paragraph>
        <Radio.Group
          onChange={(e) => setCancelReason(e.target.value)}
          value={cancelReason}
          style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}
        >
          <Radio value="too_expensive">Too expensive</Radio>
          <Radio value="missing_features">Missing features I need</Radio>
          <Radio value="found_alternative">Found a better alternative</Radio>
          <Radio value="no_longer_needed">No longer needed</Radio>
          <Radio value="other">Other</Radio>
        </Radio.Group>
        <Input.TextArea
          placeholder="Tell us more (optional)"
          value={cancelDetail}
          onChange={(e) => setCancelDetail(e.target.value)}
          rows={3}
          style={{ marginBottom: 16 }}
        />
        <Space>
          <Button onClick={() => { setShowCancelModal(false); setCancelReason(""); setCancelDetail(""); }}>
            Keep My Account
          </Button>
          <Button
            danger
            type="primary"
            loading={cancelling}
            disabled={!cancelReason}
            onClick={() => {
              cancelAccount(
                { reason: cancelReason, survey_answers: { detail: cancelDetail } },
                {
                  onSuccess: () => {
                    setShowCancelModal(false);
                    setCancelReason("");
                    setCancelDetail("");
                    message.success("Account cancellation scheduled. You have access until end of billing period.");
                  },
                  onError: () => {
                    message.error("Failed to cancel account. Please try again.");
                  },
                }
              );
            }}
          >
            Confirm Cancellation
          </Button>
        </Space>
      </Modal>
    </div>
  );
};

// ─── Notifications Tab ────────────────────────────────────────────────────────
const NOTIFICATION_ROWS: { key: keyof INotificationPreferences; label: string }[] = [
  { key: "job_assigned",    label: "Job Assigned to Me"  },
  { key: "job_completed",   label: "Job Completed"       },
  { key: "job_overdue",     label: "Overdue Jobs"        },
  { key: "invoice_created", label: "Invoice Created"     },
  { key: "invoice_paid",    label: "Invoice Paid"        },
  { key: "quote_accepted",  label: "Quote Accepted"      },
  { key: "low_stock",       label: "Low Stock Alert"     },
  { key: "new_message",     label: "New Message"         },
];

const DEFAULT_PREFS: INotificationPreferences = {
  job_assigned:    { email_enabled: true,  push_enabled: true,  sms_enabled: false },
  job_completed:   { email_enabled: true,  push_enabled: true,  sms_enabled: false },
  job_overdue:     { email_enabled: true,  push_enabled: true,  sms_enabled: false },
  invoice_created: { email_enabled: true,  push_enabled: false, sms_enabled: false },
  invoice_paid:    { email_enabled: true,  push_enabled: true,  sms_enabled: false },
  quote_accepted:  { email_enabled: true,  push_enabled: true,  sms_enabled: false },
  low_stock:       { email_enabled: true,  push_enabled: false, sms_enabled: false },
  new_message:     { email_enabled: false, push_enabled: true,  sms_enabled: false },
};

const NotificationsTab = () => {
  const messageApi = useMessageApi();
  const { data: prefsRes, isLoading: prefsLoading, isError: prefsError, refetch: retryPrefs } = useNotificationPreferences();
  const [localPrefs, setLocalPrefs] = useState<INotificationPreferences>(DEFAULT_PREFS);

  useEffect(() => {
    if (prefsRes?.data) {
      setLocalPrefs(prefsRes.data);
    }
  }, [prefsRes]);

  const { mutate: savePrefs, isPending: saving } = useUpdateNotificationPreferences({
    onSuccess: () => messageApi.success("Notification preferences saved."),
    onError: () => messageApi.error("Failed to save preferences. Please try again."),
  });

  const handleToggle = (
    rowKey: keyof INotificationPreferences,
    channel: "email_enabled" | "push_enabled" | "sms_enabled",
    value: boolean
  ) => {
    setLocalPrefs((prev) => ({
      ...prev,
      [rowKey]: { ...prev[rowKey], [channel]: value },
    }));
  };

  const handleSave = () => {
    savePrefs(localPrefs);
  };

  if (prefsLoading) {
    return (
      <SettingsCard>
        <SectionHeader title="Notification Preferences" sub="Control how and when alerts are sent" />
        <div className="flex justify-center py-8"><Spin /></div>
      </SettingsCard>
    );
  }

  if (prefsError) {
    return (
      <SettingsCard>
        <SectionHeader title="Notification Preferences" sub="Control how and when alerts are sent" />
        <div className="py-4">
          <div className="flex flex-col items-center gap-3 py-6 border border-red-100 rounded-xl bg-red-50">
            <p className="text-sm text-red-600 font-medium">Could not load preferences</p>
            <Button size="small" onClick={() => retryPrefs()}>Retry</Button>
          </div>
        </div>
      </SettingsCard>
    );
  }

  return (
    <SettingsCard>
      <SectionHeader title="Notification Preferences" sub="Control how and when alerts are sent" />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-[11px] text-slate-400 font-semibold uppercase tracking-wide border-b border-slate-100">
              <th className="text-left px-4 py-3">Notification Type</th>
              <th className="text-center py-3 px-4">Email</th>
              <th className="text-center py-3 px-4">Push</th>
              <th className="text-center py-3 px-4">
                <Tooltip title="SMS requires Twilio setup">
                  <span className="cursor-help border-b border-dashed border-slate-300">SMS</span>
                </Tooltip>
              </th>
            </tr>
          </thead>
          <tbody>
            {NOTIFICATION_ROWS.map(({ key, label }) => (
              <tr key={key} className="border-t border-slate-50 hover:bg-slate-50/70 transition-colors">
                <td className="px-4 py-3 font-medium text-slate-700">{label}</td>
                <td className="py-3 px-4 text-center">
                  <Switch
                    size="small"
                    checked={localPrefs[key]?.email_enabled ?? false}
                    onChange={(v) => handleToggle(key, "email_enabled", v)}
                  />
                </td>
                <td className="py-3 px-4 text-center">
                  <Switch
                    size="small"
                    checked={localPrefs[key]?.push_enabled ?? false}
                    onChange={(v) => handleToggle(key, "push_enabled", v)}
                  />
                </td>
                <td className="py-3 px-4 text-center">
                  <Tooltip title="SMS requires Twilio setup">
                    <Switch size="small" disabled checked={false} />
                  </Tooltip>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-5 pt-4 border-t border-slate-100">
        <Button type="primary" onClick={handleSave} loading={saving}>
          Save Preferences
        </Button>
      </div>
    </SettingsCard>
  );
};

// ─── Integration Hub — Connect Drawer ─────────────────────────────────────────
const CATEGORY_LABELS: Record<string, string> = {
  ai:            "AI & Automation",
  accounting:    "Accounting",
  documents:     "Documents & Signatures",
  calendar:      "Calendar & Productivity",
  notifications: "Communications",
  payments:      "Payments",
  maps:          "Maps & Location",
  sms:           "SMS",
  email:         "Email",
};

const CATEGORY_ORDER = ["ai", "accounting", "documents", "calendar", "notifications", "payments", "maps", "sms", "email"];

const AVATAR_COLORS: Record<string, string> = {
  ai:            "bg-violet-600",
  accounting:    "bg-blue-600",
  documents:     "bg-amber-600",
  calendar:      "bg-red-500",
  notifications: "bg-orange-500",
  payments:      "bg-emerald-600",
  maps:          "bg-green-600",
  sms:           "bg-red-600",
  email:         "bg-slate-600",
};

interface ConnectDrawerProps {
  item: IIntegrationCatalogItem | null;
  open: boolean;
  onClose: () => void;
}

const ConnectDrawer = ({ item, open, onClose }: ConnectDrawerProps) => {
  const messageApi = useMessageApi();
  const [form] = Form.useForm();
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  const { mutate: connect, isPending: connecting } = useConnectIntegration();
  const { mutate: testInteg, isPending: testing } = useTestIntegration();
  const { mutate: getXeroUrl, isPending: xeroUrlPending } = useXeroAuthUrl();
  const { mutate: getQBUrl, isPending: qbUrlPending } = useQBAuthUrl();
  const { mutate: getGCalUrl, isPending: gcalUrlPending } = useGCalAuthUrl();

  useEffect(() => {
    if (open) { form.resetFields(); setTestResult(null); }
  }, [open, item, form]);

  if (!item) return null;

  const isOAuth = item.auth_type === 'oauth2';
  const isConfig = item.auth_type === 'config';
  const isSMTP = item.slug === 'smtp';
  const isTwilio = item.slug === 'twilio';

  // Only these three OAuth integrations actually have a working backend flow
  // (auth-url + callback + status + disconnect) wired up — see
  // xeroIntegration/quickbooksIntegration/googleCalendarIntegration modules.
  // The rest of the oauth2-typed catalog entries (slack, microsoft-365,
  // freeagent) have no implementation yet, so they keep the "contact
  // support" placeholder below instead of a dead button.
  const OAUTH_CONNECT_HANDLERS: Record<string, { mutate: typeof getXeroUrl; pending: boolean }> = {
    xero: { mutate: getXeroUrl, pending: xeroUrlPending },
    quickbooks: { mutate: getQBUrl, pending: qbUrlPending },
    "google-calendar": { mutate: getGCalUrl, pending: gcalUrlPending },
  };
  const oauthHandler = OAUTH_CONNECT_HANDLERS[item.slug];

  const handleOAuthConnect = () => {
    if (!oauthHandler) return;
    oauthHandler.mutate(undefined, {
      onSuccess: (res: any) => {
        const url = res?.data?.url;
        if (url) {
          window.location.href = url;
        } else {
          messageApi.error("Could not start authorization — no redirect URL returned.");
        }
      },
      onError: (e: any) => {
        messageApi.error(e?.response?.data?.message || `Failed to start ${item.name} authorization.`);
      },
    });
  };

  const handleSave = () => {
    form.validateFields().then((values) => {
      let api_key: string | undefined;
      let config: Record<string, any> | undefined;

      if (isTwilio) {
        api_key = values.auth_token;
        config = { account_sid: values.account_sid };
      } else if (isSMTP) {
        config = {
          host: values.host,
          port: values.port,
          user: values.user,
          pass: values.pass,
          from_name: values.from_name,
          from_email: values.from_email,
        };
      } else {
        api_key = values.api_key;
      }

      connect({ slug: item.slug, api_key, config }, {
        onSuccess: () => {
          messageApi.success(`${item.name} connected successfully.`);
          // Auto-test if api_key type
          if (item.auth_type === 'api_key' && api_key) {
            testInteg(item.slug, {
              onSuccess: (res: any) => {
                const result = res?.data?.data ?? res?.data ?? res;
                setTestResult({ ok: result?.ok ?? true, message: result?.message ?? "Connected." });
              },
              onError: () => setTestResult({ ok: false, message: "Test failed. Key saved but could not verify." }),
            });
          } else {
            setTestResult({ ok: true, message: `${item.name} connected. Config saved.` });
          }
        },
        onError: (e: any) => messageApi.error(e?.response?.data?.message || "Failed to connect."),
      });
    });
  };

  return (
    <Drawer
      title={
        <div className="flex items-center gap-3">
          {item.logo_url ? (
            <img src={item.logo_url} alt={item.name} className="w-7 h-7 object-contain" />
          ) : (
            <div className={`w-7 h-7 rounded-md ${AVATAR_COLORS[item.category] ?? 'bg-slate-500'} flex items-center justify-center text-white text-xs font-bold`}>
              {item.name.charAt(0)}
            </div>
          )}
          <span>Connect {item.name}</span>
        </div>
      }
      open={open}
      onClose={onClose}
      width={480}
      footer={
        !isOAuth ? (
          <div className="flex justify-end gap-2">
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" loading={connecting || testing} onClick={handleSave}>
              Save & Test Connection
            </Button>
          </div>
        ) : oauthHandler ? (
          <div className="flex justify-end gap-2">
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" loading={oauthHandler.pending} onClick={handleOAuthConnect}>
              Connect with {item.name}
            </Button>
          </div>
        ) : null
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-500">{item.description}</p>

        {isOAuth ? (
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-blue-700">
            <div className="font-semibold mb-1">OAuth 2.0 Authorization Required</div>
            {oauthHandler ? (
              <p className="text-blue-600 text-xs">
                Click "Connect with {item.name}" below to be redirected to {item.name} to authorize ARRUK access. You will need to have a {item.name} account set up.
              </p>
            ) : (
              <>
                <p className="text-blue-600 text-xs mb-3">
                  Click below to be redirected to {item.name} to authorize ARRUK access. You will need to have a {item.name} account set up.
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-700 text-xs">
                  OAuth setup for {item.name} requires Client ID and Client Secret to be configured by your ARRUK administrator. Contact support to enable this integration.
                </div>
              </>
            )}
          </div>
        ) : (
          <Form form={form} layout="vertical" size="middle">
            {isTwilio && (
              <>
                <Form.Item name="account_sid" label="Account SID" rules={[{ required: true, message: "Account SID is required" }]}>
                  <Input placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" />
                </Form.Item>
                <Form.Item name="auth_token" label="Auth Token" rules={[{ required: true, message: "Auth Token is required" }]}>
                  <Input.Password placeholder="Your Twilio Auth Token" />
                </Form.Item>
              </>
            )}

            {isSMTP && (
              <>
                <div className="grid grid-cols-2 gap-x-4">
                  <Form.Item name="host" label="SMTP Host" rules={[{ required: true }]}>
                    <Input placeholder="smtp.example.com" />
                  </Form.Item>
                  <Form.Item name="port" label="Port" initialValue={587}>
                    <InputNumber min={1} max={65535} className="w-full" />
                  </Form.Item>
                </div>
                <div className="grid grid-cols-2 gap-x-4">
                  <Form.Item name="user" label="Username" rules={[{ required: true }]}>
                    <Input placeholder="user@example.com" />
                  </Form.Item>
                  <Form.Item name="pass" label="Password" rules={[{ required: true }]}>
                    <Input.Password placeholder="SMTP password" />
                  </Form.Item>
                </div>
                <div className="grid grid-cols-2 gap-x-4">
                  <Form.Item name="from_name" label="From Name">
                    <Input placeholder="ARRUK" />
                  </Form.Item>
                  <Form.Item name="from_email" label="From Email" rules={[{ type: "email" }]}>
                    <Input placeholder="noreply@example.com" />
                  </Form.Item>
                </div>
              </>
            )}

            {!isTwilio && !isSMTP && (
              <Form.Item name="api_key" label="API Key" rules={[{ required: true, message: "API Key is required" }]}>
                <Input.Password placeholder={`Paste your ${item.name} API key`} />
              </Form.Item>
            )}
          </Form>
        )}

        {testResult && (
          <div className={`rounded-xl p-4 border text-sm ${testResult.ok ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-red-50 border-red-200 text-red-700"}`}>
            <div className="font-semibold mb-0.5">{testResult.ok ? "Connected successfully" : "Connection issue"}</div>
            <div className="text-xs">{testResult.message}</div>
          </div>
        )}
      </div>
    </Drawer>
  );
};

// ─── Integration Card ──────────────────────────────────────────────────────────
const IntegrationCard = ({
  item,
  onConnect,
  onDisconnect,
}: {
  item: IIntegrationCatalogItem;
  onConnect: (item: IIntegrationCatalogItem) => void;
  onDisconnect: (slug: string) => void;
}) => {
  const { mutate: testInteg, isPending: testing } = useTestIntegration();
  const messageApi = useMessageApi();

  const handleTest = () => {
    testInteg(item.slug, {
      onSuccess: (res: any) => {
        const result = res?.data?.data ?? res?.data ?? res;
        if (result?.ok) {
          messageApi.success(result?.message || "Connection is working.");
        } else {
          messageApi.warning(result?.message || "Connection test failed.");
        }
      },
      onError: () => messageApi.error("Test failed."),
    });
  };

  const avatarColor = AVATAR_COLORS[item.category] ?? 'bg-slate-500';

  return (
    <div className="bg-white border border-slate-100 rounded-xl p-4 flex flex-col gap-3 shadow-sm hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">
          {item.logo_url ? (
            <img
              src={item.logo_url}
              alt={item.name}
              className="w-10 h-10 object-contain rounded-lg border border-slate-100 p-0.5"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
                (e.currentTarget.nextSibling as HTMLElement).style.display = 'flex';
              }}
            />
          ) : null}
          <div
            className={`w-10 h-10 rounded-lg ${avatarColor} flex items-center justify-center text-white text-sm font-bold ${item.logo_url ? 'hidden' : ''}`}
          >
            {item.name.charAt(0)}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-800 text-sm">{item.name}</span>
            {item.is_connected ? (
              <span className="text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded-full">
                Connected
              </span>
            ) : item.connection_status === 'error' ? (
              <span className="text-[10px] font-semibold bg-red-50 text-red-600 border border-red-200 px-1.5 py-0.5 rounded-full">
                Error
              </span>
            ) : null}
          </div>
          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full mt-0.5 inline-block ${avatarColor} bg-opacity-10 text-slate-600`}>
            {CATEGORY_LABELS[item.category] ?? item.category}
          </span>
        </div>
      </div>

      {/* Description */}
      <p className="text-xs text-slate-500 line-clamp-2 flex-1">{item.description}</p>

      {/* Actions */}
      <div className="flex gap-2 pt-1 border-t border-slate-50">
        {item.is_connected ? (
          <>
            <Button size="small" loading={testing} onClick={handleTest} className="text-xs">
              Test
            </Button>
            <Button size="small" onClick={() => onConnect(item)} className="text-xs flex-1">
              Configure
            </Button>
            <Popconfirm
              title={`Disconnect ${item.name}?`}
              description="API key will be removed. You can reconnect at any time."
              onConfirm={() => onDisconnect(item.slug)}
              okText="Disconnect"
              okButtonProps={{ danger: true }}
            >
              <Button size="small" danger className="text-xs">
                Disconnect
              </Button>
            </Popconfirm>
          </>
        ) : (
          <Button
            size="small"
            type="primary"
            className="text-xs w-full"
            onClick={() => onConnect(item)}
          >
            Connect
          </Button>
        )}
      </div>
    </div>
  );
};

// ─── Integrations Tab ─────────────────────────────────────────────────────────
const IntegrationsTab = () => {
  const messageApi = useMessageApi();
  const [drawerItem, setDrawerItem] = useState<IIntegrationCatalogItem | null>(null);

  const { data: hubRes, isLoading: hubLoading } = useIntegrationHub();
  const grouped = hubRes?.data?.grouped ?? {};

  const { mutate: disconnectHub, isPending: disconnecting } = useDisconnectHubIntegration();

  // Keep legacy hooks mounted but unused visually (they still work for OAuth accounting)
  useIntegrations();
  useIntegrationConnectUrl();
  useDisconnectIntegration({ onSuccess: () => {}, onError: () => {} });

  const handleDisconnect = (slug: string) => {
    disconnectHub(slug, {
      onSuccess: () => messageApi.success("Disconnected."),
      onError: () => messageApi.error("Failed to disconnect."),
    });
  };

  const orderedCategories = [
    ...CATEGORY_ORDER.filter((c) => grouped[c]),
    ...Object.keys(grouped).filter((c) => !CATEGORY_ORDER.includes(c)),
  ];

  return (
    <div className="space-y-6">
      {hubLoading ? (
        <SettingsCard>
          <Skeleton active paragraph={{ rows: 6 }} />
        </SettingsCard>
      ) : orderedCategories.length === 0 ? (
        <SettingsCard>
          <div className="flex flex-col items-center py-10 gap-4">
            <Plug size={32} className="text-slate-300" />
            <p className="text-sm text-slate-400">No integrations available.</p>
          </div>
        </SettingsCard>
      ) : (
        orderedCategories.map((category) => {
          const items: IIntegrationCatalogItem[] = grouped[category] ?? [];
          return (
            <div key={category}>
              <h3 className="text-sm font-semibold text-slate-600 mb-3 flex items-center gap-2">
                <span>{CATEGORY_LABELS[category] ?? category}</span>
                <span className="text-xs font-normal text-slate-400">({items.length})</span>
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((item) => (
                  <IntegrationCard
                    key={item.slug}
                    item={item}
                    onConnect={setDrawerItem}
                    onDisconnect={handleDisconnect}
                  />
                ))}
              </div>
            </div>
          );
        })
      )}

      <ConnectDrawer
        item={drawerItem}
        open={!!drawerItem}
        onClose={() => setDrawerItem(null)}
      />
    </div>
  );
};

// ─── Job Types Tab ────────────────────────────────────────────────────────────
const JobTypesTab = () => {
  const { data } = useJobTypeList({ page: 1, limit: 100 });
  const jobTypes: IJobType[] = (data?.data as IJobType[] | undefined) ?? [];
  return (
    <SettingsCard>
      <SectionHeader title="Job Types" sub="Service types configured for your company" />
      {jobTypes.length === 0 ? (
        <div className="text-center py-8 text-slate-400 text-sm">No job types configured. Add them from the Job Config page.</div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-[11px] text-slate-400 font-semibold uppercase tracking-wide border-b border-slate-100">
              <th className="text-left px-4 py-3">Job Type</th>
              <th className="text-left py-3">Default Duration</th>
              <th className="text-left py-3">Requires</th>
              <th className="text-left py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {jobTypes.map((jt) => (
              <tr key={jt.id} className="border-t border-slate-50 hover:bg-slate-50/70 transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2.5">
                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: jt.colour ?? "#94a3b8" }} />
                    <span className="font-medium text-slate-800">{jt.name}</span>
                  </div>
                </td>
                <td className="py-3 text-slate-500 text-xs">
                  {jt.default_duration_mins ? `${jt.default_duration_mins} min` : "—"}
                </td>
                <td className="py-3 text-xs text-slate-500">
                  {[
                    jt.requires_cert && "Cert",
                    jt.requires_parts && "Parts",
                    jt.requires_photo && "Photo",
                    jt.requires_signature && "Signature",
                  ].filter(Boolean).join(", ") || "—"}
                </td>
                <td className="py-3">
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${jt.is_active ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-slate-500 bg-slate-50 border-slate-200"}`}>
                    {jt.is_active ? "Active" : "Inactive"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </SettingsCard>
  );
};

// ─── API Tab ──────────────────────────────────────────────────────────────────
const ApiTab = () => {
  return (
    <div className="space-y-5">
      <SettingsCard>
        <SectionHeader title="API Keys" sub="Use API keys to integrate ARRUK with external systems" />
        <div className="flex flex-col items-center py-10 gap-4">
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={null} />
          <p className="text-sm text-slate-500 text-center max-w-sm">
            API key management is coming soon. Contact support for API access.
          </p>
          <Button onClick={() => window.open("mailto:support@arruk.co.uk?subject=API Access Request", "_blank")}>
            Contact Support
          </Button>
        </div>
      </SettingsCard>
      <SettingsCard>
        <SectionHeader title="Webhooks" sub="Send real-time event data to your systems" />
        <div className="flex flex-col items-center py-10 gap-4">
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={null} />
          <p className="text-sm text-slate-500 text-center max-w-sm">
            Webhook configuration is coming soon. Contact support to request early access.
          </p>
          <Button onClick={() => window.open("mailto:support@arruk.co.uk?subject=Webhook Configuration", "_blank")}>
            Contact Support
          </Button>
        </div>
      </SettingsCard>
    </div>
  );
};

// ─── Branches Tab ────────────────────────────────────────────────────────────
const BranchesTab = () => {
  const messageApi = useMessageApi();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<IBranch | null>(null);
  const [form] = Form.useForm();
  const [page, setPage] = useState(1);
const [search, setSearch] = useState("");

const queryData = useMemo(
  () => ({
    page,
    limit: 20,
    ...(search && { search }),
  }),
  [page, search]
);

const { data: listRes, isLoading, refetchWithQuery: refetchBranchList } = useBranchList(queryData);

useEffect(() => {
  refetchBranchList({
    page,
    limit: 20,
    ...(search && { search }),
  } as any);
}, [page, search]);
  const branches = listRes?.data ?? [];
  const total = listRes?.extra?.total ?? 0;

  const { mutate: create, isPending: creating } = useBranchCreate({
    qData: queryData,
    onSuccess: () => { messageApi.success("Branch created."); setDrawerOpen(false); form.resetFields(); },
    onError: (e: any) => messageApi.error(e?.response?.data?.message || e?.message || "Failed to create branch."),
  });
  const { mutate: update, isPending: updating } = useBranchUpdate({
    qData: queryData,
    onSuccess: () => { messageApi.success("Branch updated."); setDrawerOpen(false); setEditing(null); form.resetFields(); },
    onError: (e: any) => messageApi.error(e?.response?.data?.message || e?.message || "Failed to update branch."),
  });
  const { mutate: remove } = useBranchDelete({
    qData: queryData,
    onSuccess: () => messageApi.success("Branch deleted."),
    onError: (e: any) => messageApi.error(e?.response?.data?.message || e?.message || "Failed to delete branch."),
  });

  const openCreate = () => { setEditing(null); form.resetFields(); setDrawerOpen(true); };
  const openEdit = (b: IBranch) => { setEditing(b); form.setFieldsValue(b); setDrawerOpen(true); };
  const onSubmit = () => {
    form.validateFields().then((values) => {
      if (editing) update({ ...values, id: editing.id });
      else create(values);
    });
  };

  const columns = [
    { title: "Name", dataIndex: "name", key: "name", render: (v: string, r: IBranch) => <span className="font-medium text-slate-800">{v}{r.code ? <span className="ml-2 text-xs text-slate-400">({r.code})</span> : null}</span> },
    { title: "Phone", dataIndex: "phone", key: "phone", render: (v: string) => v || <span className="text-slate-300">—</span> },
    { title: "Email", dataIndex: "email", key: "email", render: (v: string) => v || <span className="text-slate-300">—</span> },
    { title: "Manager", dataIndex: "manager_name", key: "manager_name", render: (v: string) => v?.trim() || <span className="text-slate-300">—</span> },
    { title: "Status", dataIndex: "is_active", key: "is_active", render: (v: boolean) => <Tag color={v ? "green" : "default"}>{v ? "Active" : "Inactive"}</Tag> },
    {
      title: "", key: "actions", width: 100,
      render: (_: any, r: IBranch) => (
        <div className="flex gap-2">
          <button onClick={() => openEdit(r)} className="text-xs text-blue-600 hover:underline">Edit</button>
          <Popconfirm title="Delete this branch?" onConfirm={() => remove({ id: r.id })} okText="Delete" okButtonProps={{ danger: true }}>
            <button className="text-xs text-red-500 hover:underline">Delete</button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <>
      <SettingsCard>
        <SectionHeader title="Branches" sub="Manage your company's branch locations" action={
          <Button type="primary" icon={<Plus size={14} />} onClick={openCreate} className="h-8 text-sm rounded-lg flex items-center gap-1.5">Add Branch</Button>
        } />
        <Input.Search
  placeholder="Search branches…"
  allowClear
  onSearch={(v) => {
    setSearch(v);
    setPage(1);
  }}
  onChange={(e) => {
    if (!e.target.value) {
      setSearch("");
      setPage(1);
    }
  }}
/>

<Table
  dataSource={branches}
  columns={columns}
  loading={isLoading}
  rowKey="id"
  pagination={{
    current: page,
    total,
    pageSize: 20,
    showSizeChanger: false,
    onChange: setPage,
  }}
/>
      </SettingsCard>
      <Drawer title={editing ? "Edit Branch" : "New Branch"} open={drawerOpen} onClose={() => { setDrawerOpen(false); setEditing(null); form.resetFields(); }} width={480}
        footer={<div className="flex justify-end gap-2"><Button onClick={() => setDrawerOpen(false)}>Cancel</Button><Button type="primary" loading={creating || updating} onClick={onSubmit}>{editing ? "Save" : "Create"}</Button></div>}>
        <Form form={form} layout="vertical" size="middle">
          <Form.Item name="name" label="Branch Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="code" label="Code (optional)"><Input placeholder="e.g. LON-01" /></Form.Item>
          <Form.Item name="address" label="Address"><Input.TextArea rows={2} /></Form.Item>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="phone" label="Phone"><Input /></Form.Item>
            <Form.Item name="email" label="Email" rules={[{ type: "email" }]}><Input /></Form.Item>
          </div>
          <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked"><Switch /></Form.Item>
        </Form>
      </Drawer>
    </>
  );
};

// ─── Divisions Tab ────────────────────────────────────────────────────────────
const DivisionsTab = () => {
  const messageApi = useMessageApi();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<IDivision | null>(null);
  const [form] = Form.useForm();
  const divSearchTimeout = useRef<ReturnType<typeof setTimeout>>();

  const { data: listRes, isLoading, refetchWithQuery: divRefetchWithQuery } = useDivisionList({ page: 1, limit: 20 });

  const onDivisionSearch = (val: string) => {
    clearTimeout(divSearchTimeout.current);
    divSearchTimeout.current = setTimeout(() => {
      divRefetchWithQuery({ search: val, page: 1 });
    }, 350);
  };
  const { data: branchRes } = useBranchList({ page: 1, limit: 200 } as any);
  const divisions = listRes?.data ?? [];
  const branchOptions = (branchRes?.data ?? []).map((b: IBranch) => ({ value: b.id, label: b.name }));

  const { mutate: create, isPending: creating } = useDivisionCreate({
    onSuccess: () => { messageApi.success("Division created."); setDrawerOpen(false); form.resetFields(); },
    onError: (e: any) => messageApi.error(e?.response?.data?.message || e?.message || "Failed to create division."),
  });
  const { mutate: update, isPending: updating } = useDivisionUpdate({
    onSuccess: () => { messageApi.success("Division updated."); setDrawerOpen(false); setEditing(null); form.resetFields(); },
    onError: (e: any) => messageApi.error(e?.response?.data?.message || e?.message || "Failed to update division."),
  });
  const { mutate: remove } = useDivisionDelete({
    onSuccess: () => messageApi.success("Division deleted."),
    onError: (e: any) => messageApi.error(e?.response?.data?.message || e?.message || "Failed to delete division."),
  });

  const openCreate = () => { setEditing(null); form.resetFields(); setDrawerOpen(true); };
  const openEdit = (d: IDivision) => { setEditing(d); form.setFieldsValue(d); setDrawerOpen(true); };
  const onSubmit = () => {
    form.validateFields().then((values) => {
      if (editing) update({ ...values, id: editing.id });
      else create(values);
    });
  };

  const columns = [
    { title: "Name", dataIndex: "name", key: "name", render: (v: string, r: IDivision) => <span className="font-medium text-slate-800">{v}{r.code ? <span className="ml-2 text-xs text-slate-400">({r.code})</span> : null}</span> },
    { title: "Branch", dataIndex: "branch_name", key: "branch_name", render: (v: string) => v || <span className="text-slate-300">—</span> },
    { title: "Manager", dataIndex: "manager_name", key: "manager_name", render: (v: string) => v?.trim() || <span className="text-slate-300">—</span> },
    { title: "Status", dataIndex: "is_active", key: "is_active", render: (v: boolean) => <Tag color={v ? "green" : "default"}>{v ? "Active" : "Inactive"}</Tag> },
    {
      title: "", key: "actions", width: 100,
      render: (_: any, r: IDivision) => (
        <div className="flex gap-2">
          <button onClick={() => openEdit(r)} className="text-xs text-blue-600 hover:underline">Edit</button>
          <Popconfirm title="Delete this division?" onConfirm={() => remove({ id: r.id })} okText="Delete" okButtonProps={{ danger: true }}>
            <button className="text-xs text-red-500 hover:underline">Delete</button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <>
      <SettingsCard>
        <SectionHeader title="Divisions" sub="Manage organisational divisions within your company" action={
          <Button type="primary" icon={<Plus size={14} />} onClick={openCreate} className="h-8 text-sm rounded-lg flex items-center gap-1.5">Add Division</Button>
        } />
        <Input.Search placeholder="Search divisions…" onSearch={onDivisionSearch} onChange={(e) => { if (!e.target.value) onDivisionSearch(""); }} className="mb-4" allowClear />
        <Table dataSource={divisions} columns={columns} loading={isLoading} rowKey="id" size="small"
          pagination={{ total: listRes?.extra?.total, pageSize: 20, showSizeChanger: false, onChange: (p) => divRefetchWithQuery({ page: p }) }} />
      </SettingsCard>
      <Drawer title={editing ? "Edit Division" : "New Division"} open={drawerOpen} onClose={() => { setDrawerOpen(false); setEditing(null); form.resetFields(); }} width={480}
        footer={<div className="flex justify-end gap-2"><Button onClick={() => setDrawerOpen(false)}>Cancel</Button><Button type="primary" loading={creating || updating} onClick={onSubmit}>{editing ? "Save" : "Create"}</Button></div>}>
        <Form form={form} layout="vertical" size="middle">
          <Form.Item name="name" label="Division Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="code" label="Code (optional)"><Input placeholder="e.g. SALES" /></Form.Item>
          <Form.Item name="branch_id" label="Branch (optional)">
            <Select options={branchOptions} allowClear placeholder="Not branch-specific" />
          </Form.Item>
          <Form.Item name="description" label="Description"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked"><Switch /></Form.Item>
        </Form>
      </Drawer>
    </>
  );
};

// ─── Holidays Tab ─────────────────────────────────────────────────────────────
const HOLIDAY_TYPE_LABELS: Record<THolidayType, string> = {
  public_holiday: "Public Holiday",
  company_holiday: "Company Holiday",
  optional: "Optional",
  regional: "Regional",
};

const HOLIDAY_TYPE_COLORS: Record<THolidayType, string> = {
  public_holiday: "blue",
  company_holiday: "green",
  optional: "orange",
  regional: "purple",
};

const HolidaysTab = () => {
  const messageApi = useMessageApi();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<IHoliday | null>(null);
  const [form] = Form.useForm();
  const [yearFilter, setYearFilter] = useState<number>(new Date().getFullYear());

  const { data: listRes, isLoading, refetchWithQuery } = useHolidayList({ page: 1, limit: 50 });
  const { data: branchRes } = useBranchList({ page: 1, limit: 200 } as any);
  const holidays = listRes?.data ?? [];
  const branchOptions = (branchRes?.data ?? []).map((b: IBranch) => ({ value: b.id, label: b.name }));

  const { mutate: create, isPending: creating } = useHolidayCreate({
    onSuccess: () => { messageApi.success("Holiday created."); setDrawerOpen(false); form.resetFields(); },
    onError: (e: any) => messageApi.error(e?.response?.data?.message || e?.message || "Failed to create holiday."),
  });
  const { mutate: update, isPending: updating } = useHolidayUpdate({
    onSuccess: () => { messageApi.success("Holiday updated."); setDrawerOpen(false); setEditing(null); form.resetFields(); },
    onError: (e: any) => messageApi.error(e?.response?.data?.message || e?.message || "Failed to update holiday."),
  });
  const { mutate: remove } = useHolidayDelete({
    onSuccess: () => messageApi.success("Holiday deleted."),
    onError: (e: any) => messageApi.error(e?.response?.data?.message || e?.message || "Failed to delete holiday."),
  });

  const [importingUk, setImportingUk] = useState(false);
  const importUkHolidays = async () => {
    setImportingUk(true);
    try {
      const res = await axiosInstance.post("/company-config/holidays/import-uk");
      messageApi.success(res.data?.message || "UK holidays imported.");
      refetchWithQuery({ year: yearFilter, page: 1 });
    } catch (e: any) {
      messageApi.error(e?.response?.data?.message || e?.message || "Failed to import UK holidays.");
    } finally {
      setImportingUk(false);
    }
  };

  const openCreate = () => { setEditing(null); form.resetFields(); setDrawerOpen(true); };
  const openEdit = (h: IHoliday) => {
    setEditing(h);
    form.setFieldsValue({ ...h, date: h.date ? dayjs(h.date) : null });
    setDrawerOpen(true);
  };
  const onSubmit = () => {
    form.validateFields().then((values) => {
      const payload = { ...values, date: values.date ? values.date.format("YYYY-MM-DD") : undefined };
      if (editing) update({ ...payload, id: editing.id });
      else create(payload);
    });
  };

  const columns = [
    { title: "Name", dataIndex: "name", key: "name", render: (v: string) => <span className="font-medium text-slate-800">{v}</span> },
    { title: "Date", dataIndex: "date", key: "date", render: (v: string) => dayjs(v).format("DD MMM YYYY") },
    { title: "Type", dataIndex: "holiday_type", key: "holiday_type", render: (v: THolidayType) => <Tag color={HOLIDAY_TYPE_COLORS[v]}>{HOLIDAY_TYPE_LABELS[v]}</Tag> },
    { title: "Branch", dataIndex: "branch_name", key: "branch_name", render: (v: string) => v || <span className="text-slate-300">Company-wide</span> },
    { title: "Recurring", dataIndex: "is_recurring", key: "is_recurring", render: (v: boolean) => v ? <Tag color="blue">Yes</Tag> : <span className="text-slate-300">—</span> },
    {
      title: "", key: "actions", width: 100,
      render: (_: any, r: IHoliday) => (
        <div className="flex gap-2">
          <button onClick={() => openEdit(r)} className="text-xs text-blue-600 hover:underline">Edit</button>
          <Popconfirm title="Delete this holiday?" onConfirm={() => remove({ id: r.id })} okText="Delete" okButtonProps={{ danger: true }}>
            <button className="text-xs text-red-500 hover:underline">Delete</button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const yearOptions = Array.from({ length: 4 }, (_, i) => {
    const y = dayjs().year() - 1 + i;
    return { value: y, label: String(y) };
  });

  return (
    <>
      <SettingsCard>
        <SectionHeader title="Company Holidays" sub="Manage public and company-specific holidays" action={
          <Space>
            <Button loading={importingUk} onClick={importUkHolidays} className="h-8 text-sm rounded-lg flex items-center gap-1.5">
              Import UK Bank Holidays
            </Button>
            <Button type="primary" icon={<Plus size={14} />} onClick={openCreate} className="h-8 text-sm rounded-lg flex items-center gap-1.5">Add Holiday</Button>
          </Space>
        } />
        <div className="flex gap-3 mb-4">
          <Select options={yearOptions} value={yearFilter} onChange={(v) => { setYearFilter(v); refetchWithQuery({ year: v, page: 1 }); }} className="w-28" />
          <Input.Search placeholder="Search…" onSearch={(v) => refetchWithQuery({ search: v, page: 1 })} onChange={(e) => !e.target.value && refetchWithQuery({ search: "", page: 1 })} allowClear className="flex-1" />
        </div>
        <Table dataSource={holidays} columns={columns} loading={isLoading} rowKey="id" size="small"
          pagination={{ total: listRes?.extra?.total, pageSize: 50, showSizeChanger: false, onChange: (p) => refetchWithQuery({ page: p }) }} />
      </SettingsCard>
      <Drawer title={editing ? "Edit Holiday" : "New Holiday"} open={drawerOpen} onClose={() => { setDrawerOpen(false); setEditing(null); form.resetFields(); }} width={480}
        footer={<div className="flex justify-end gap-2"><Button onClick={() => setDrawerOpen(false)}>Cancel</Button><Button type="primary" loading={creating || updating} onClick={onSubmit}>{editing ? "Save" : "Create"}</Button></div>}>
        <Form form={form} layout="vertical" size="middle" initialValues={{ holiday_type: "company_holiday", is_recurring: false, affects_scheduling: true, affects_attendance: true, affects_jobs: false, is_active: true }}>
          <Form.Item name="name" label="Holiday Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="date" label="Date" rules={[{ required: true }]}>
            <DatePicker
              className="w-full"
              format="DD MMM YYYY"
              minDate={dayjs().subtract(1, 'year').startOf('year')}
              maxDate={dayjs().add(2, 'year').endOf('year')}
            />
          </Form.Item>
          <Form.Item name="holiday_type" label="Type">
            <Select options={Object.entries(HOLIDAY_TYPE_LABELS).map(([v, l]) => ({ value: v, label: l }))} />
          </Form.Item>
          <Form.Item name="branch_id" label="Branch (leave blank for company-wide)">
            <Select options={branchOptions} allowClear placeholder="Company-wide" />
          </Form.Item>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="is_recurring" label="Recurring Annually" valuePropName="checked"><Switch /></Form.Item>
            <Form.Item name="affects_scheduling" label="Affects Scheduling" valuePropName="checked"><Switch /></Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="affects_attendance" label="Affects Attendance" valuePropName="checked"><Switch /></Form.Item>
            <Form.Item name="affects_jobs" label="Affects Jobs" valuePropName="checked"><Switch /></Form.Item>
          </div>
          <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked"><Switch /></Form.Item>
        </Form>
      </Drawer>
    </>
  );
};

// ─── Feature Overrides Tab ────────────────────────────────────────────────────
const FeatureOverridesTab = () => {
  const messageApi = useMessageApi();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<IFeatureOverride | null>(null);
  const [form] = Form.useForm();

  const { data: listRes, isLoading, refetchWithQuery } = useFeatureOverrideList({ page: 1, limit: 50 });
  const overrides = listRes?.data ?? [];

  const { mutate: set, isPending: saving } = useFeatureOverrideSet({
    onSuccess: () => { messageApi.success("Feature override saved."); setDrawerOpen(false); setEditing(null); form.resetFields(); },
    onError: (e: any) => messageApi.error(e?.message || "Failed."),
  });
  const { mutate: remove } = useFeatureOverrideRemove({
    onSuccess: () => messageApi.success("Feature override removed."),
    onError: (e: any) => messageApi.error(e?.message || "Failed."),
  });

  const openCreate = () => { setEditing(null); form.resetFields(); setDrawerOpen(true); };
  const openEdit = (f: IFeatureOverride) => {
    setEditing(f);
    form.setFieldsValue({ ...f, expires_at: f.expires_at ? dayjs(f.expires_at) : null });
    setDrawerOpen(true);
  };
const onSubmit = () => {
  form.validateFields().then((values) => {
    set({
      ...values,
      expires_at: values.expires_at
        ? values.expires_at.format("YYYY-MM-DD HH:mm:ss")
        : null,
    });
  });
};

  const columns = [
    { title: "Feature Key", dataIndex: "feature_key", key: "feature_key", render: (v: string) => <span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">{v}</span> },
    { title: "Enabled", dataIndex: "is_enabled", key: "is_enabled", render: (v: boolean) => <Tag color={v ? "green" : "red"}>{v ? "Enabled" : "Disabled"}</Tag> },
    { title: "Value", dataIndex: "value", key: "value", render: (v: string) => v ? <span className="font-mono text-xs text-slate-500 truncate max-w-32 block">{v}</span> : <span className="text-slate-300">—</span> },
    { title: "Expires", dataIndex: "expires_at", key: "expires_at", render: (v: string) => v ? <span className="text-xs text-slate-500">{dayjs(v).format("DD MMM YYYY")}</span> : <span className="text-slate-300">Never</span> },
    { title: "Reason", dataIndex: "override_reason", key: "override_reason", render: (v: string) => <span className="text-xs text-slate-400 truncate max-w-40 block">{v || "—"}</span> },
    {
      title: "", key: "actions", width: 100,
      render: (_: any, r: IFeatureOverride) => (
        <div className="flex gap-2">
          <button onClick={() => openEdit(r)} className="text-xs text-blue-600 hover:underline">Edit</button>
          <Popconfirm title="Remove this override?" onConfirm={() => remove({ feature_key: r.feature_key })} okText="Remove" okButtonProps={{ danger: true }}>
            <button className="text-xs text-red-500 hover:underline">Remove</button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <>
      <SettingsCard>
        <SectionHeader
          title="Feature Overrides"
          sub="Enable or disable specific features per company without code changes"
          action={<Button type="primary" icon={<Plus size={14} />} onClick={openCreate} className="h-8 text-sm rounded-lg flex items-center gap-1.5">Add Override</Button>}
        />
        <Input.Search placeholder="Search feature keys…" onSearch={(v) => refetchWithQuery({ search: v, page: 1 })} onChange={(e) => !e.target.value && refetchWithQuery({ search: "", page: 1 })} className="mb-4" allowClear />
        <Table dataSource={overrides} columns={columns} loading={isLoading} rowKey="id" size="small"
          pagination={{ total: listRes?.extra?.total, pageSize: 50, showSizeChanger: false, onChange: (p) => refetchWithQuery({ page: p }) }} />
      </SettingsCard>
      <Drawer title={editing ? "Edit Feature Override" : "New Feature Override"} open={drawerOpen} onClose={() => { setDrawerOpen(false); setEditing(null); form.resetFields(); }} width={480}
        footer={<div className="flex justify-end gap-2"><Button onClick={() => setDrawerOpen(false)}>Cancel</Button><Button type="primary" loading={saving} onClick={onSubmit}>{editing ? "Save" : "Create"}</Button></div>}>
        <Form form={form} layout="vertical" size="middle" initialValues={{ is_enabled: true }}>
          <Form.Item name="feature_key" label="Feature Key" rules={[{ required: true }, { pattern: /^[a-z0-9_]+$/, message: "Lowercase alphanumeric + underscores only" }]}>
            <Input placeholder="e.g. invoicing, max_users" disabled={!!editing} />
          </Form.Item>
          <Form.Item name="is_enabled" label="Enabled" valuePropName="checked"><Switch /></Form.Item>
          <Form.Item name="value" label="Value (optional JSON string)"><Input.TextArea rows={2} placeholder='e.g. {"limit":50}' /></Form.Item>
          <Form.Item name="override_reason" label="Reason"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="expires_at" label="Expires At (leave blank for permanent)">
            <DatePicker showTime className="w-full" format="YYYY-MM-DD HH:mm" />
          </Form.Item>
        </Form>
      </Drawer>
    </>
  );
};

// ─── Industry Seed Tab ────────────────────────────────────────────────────────
const INDUSTRY_ICONS: Record<string, string> = {
  pest_control: "🐛",
  fire_safety: "🔥",
  property_maintenance: "🏠",
  hvac: "❄️",
  electrical: "⚡",
  cleaning: "🧹",
  security: "🛡️",
  plumbing: "🔧",
};
const INDUSTRY_LABELS: Record<string, string> = {
  pest_control: "Pest Control",
  fire_safety: "Fire Safety",
  property_maintenance: "Property Maintenance",
  hvac: "HVAC",
  electrical: "Electrical",
  cleaning: "Cleaning",
  security: "Security",
  plumbing: "Plumbing",
};

const IndustryTab = () => {
  const messageApi = useMessageApi();
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [result, setResult] = useState<{ seeded_job_types: number; seeded_cert_templates: number; terminology: any } | null>(null);
  const [isPending, setIsPending] = useState(false);

  const seedIndustry = async (data: { industry_slug: string }) => {
    setIsPending(true);
    try {
      const res = await axiosInstance.post("/industry/seed", data);
      const d = res.data?.data;
      setResult(d);
      messageApi.success(`Industry seed applied: ${d.seeded_job_types} job types, ${d.seeded_cert_templates} cert templates added.`);
    } catch (e: any) {
      messageApi.error(e?.response?.data?.message || "Seed failed.");
    } finally {
      setIsPending(false);
    }
  };

  const slugs = Object.keys(INDUSTRY_LABELS);

  return (
    <SettingsCard>
      <SectionHeader title="Industry Configuration" sub="Select your industry to auto-seed job types and certificate templates" />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {slugs.map((slug) => (
          <button
            key={slug}
            onClick={() => setSelectedSlug(slug)}
            className={`p-4 rounded-xl border-2 text-center transition-all cursor-pointer ${
              selectedSlug === slug
                ? "border-blue-500 bg-blue-50 shadow-sm"
                : "border-slate-100 hover:border-slate-200 bg-white"
            }`}
          >
            <div className="text-2xl mb-1">{INDUSTRY_ICONS[slug]}</div>
            <div className={`text-xs font-semibold ${selectedSlug === slug ? "text-blue-700" : "text-slate-600"}`}>
              {INDUSTRY_LABELS[slug]}
            </div>
          </button>
        ))}
      </div>

      {selectedSlug && (
        <div className="border border-slate-100 rounded-xl p-4 mb-4 bg-slate-50">
          <div className="text-sm font-medium text-slate-700 mb-1">
            {INDUSTRY_ICONS[selectedSlug]} {INDUSTRY_LABELS[selectedSlug]} selected
          </div>
          <div className="text-xs text-slate-500 mb-3">
            This will add pre-configured job types and certificate templates for your industry. Existing records will not be overwritten.
          </div>
          <Button
            type="primary"
            loading={isPending}
            onClick={() => selectedSlug && seedIndustry({ industry_slug: selectedSlug })}
          >
            Apply Industry Config
          </Button>
        </div>
      )}

      {result && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm">
          <div className="font-semibold text-emerald-700 mb-1">Industry config applied successfully</div>
          <div className="text-emerald-600">
            Added {result.seeded_job_types} job types and {result.seeded_cert_templates} certificate templates.
          </div>
          {result.terminology && (
            <div className="mt-2 text-xs text-emerald-600">
              Terminology: Jobs = <strong>{result.terminology.jobs}</strong>, Workers = <strong>{result.terminology.workers}</strong>
            </div>
          )}
        </div>
      )}
    </SettingsCard>
  );
};

// ─── Security / 2FA Tab ───────────────────────────────────────────────────────
const SecurityTab = () => {
  const { user, updateUser } = useUserStore();
  const twoFaEnabled = !!user?.two_fa_enabled;

  const [enableModalOpen, setEnableModalOpen] = useState(false);
  const [disableModalOpen, setDisableModalOpen] = useState(false);

  const [qrUrl, setQrUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [qrLoading, setQrLoading] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [verifying, setVerifying] = useState(false);

  const [disablePassword, setDisablePassword] = useState("");
  const [disabling, setDisabling] = useState(false);

  const openEnableModal = async () => {
    setEnableModalOpen(true);
    setOtpValue("");
    setQrLoading(true);
    try {
      const res = await axiosInstance.get("/admin/2fa/setup");
      const d = res.data?.data ?? res.data;
      setQrUrl(d.qr ?? "");
      setSecret(d.secret ?? "");
    } catch (err: any) {
      message.error(err?.message || "Failed to load QR code");
    } finally {
      setQrLoading(false);
    }
  };

  const handleEnable = async () => {
    if (!otpValue || otpValue.length !== 6) { message.error("Enter a 6-digit code"); return; }
    setVerifying(true);
    try {
      await axiosInstance.post("/admin/2fa/enable", { token: otpValue });
      message.success("2FA enabled successfully");
      setEnableModalOpen(false);
      if (user) updateUser({ ...user, two_fa_enabled: 1 });
    } catch (err: any) {
      message.error(err?.message || "Invalid code");
    } finally {
      setVerifying(false);
    }
  };

  const handleDisable = async () => {
    if (!disablePassword) { message.error("Enter your password"); return; }
    setDisabling(true);
    try {
      await axiosInstance.post("/admin/2fa/disable", { password: disablePassword });
      message.success("2FA disabled");
      setDisableModalOpen(false);
      setDisablePassword("");
      if (user) updateUser({ ...user, two_fa_enabled: 0 });
    } catch (err: any) {
      message.error(err?.message || "Failed to disable 2FA");
    } finally {
      setDisabling(false);
    }
  };

  return (
    <>
      <SettingsCard>
        <SectionHeader title="Security" sub="Manage account security settings" />

        <div className="flex items-center justify-between p-4 border border-slate-100 rounded-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <ShieldCheck size={20} className="text-blue-600" />
            </div>
            <div>
              <div className="font-medium text-slate-800 text-sm flex items-center gap-2">
                Two-Factor Authentication
                {twoFaEnabled && (
                  <span className="text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full">
                    Active
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-400 mt-0.5">
                {twoFaEnabled
                  ? "Your account is protected with an authenticator app"
                  : "Add extra security via authenticator app"}
              </div>
            </div>
          </div>
          {twoFaEnabled ? (
            <Button danger size="small" onClick={() => { setDisablePassword(""); setDisableModalOpen(true); }}>
              Disable 2FA
            </Button>
          ) : (
            <Button type="primary" size="small" onClick={openEnableModal}>
              Enable 2FA
            </Button>
          )}
        </div>
      </SettingsCard>

      {/* Enable 2FA Modal */}
      <Modal
        title="Enable Two-Factor Authentication"
        open={enableModalOpen}
        onCancel={() => setEnableModalOpen(false)}
        footer={null}
        width={420}
      >
        {qrLoading ? (
          <div className="flex justify-center py-8"><Spin /></div>
        ) : (
          <div className="space-y-5">
            <p className="text-sm text-slate-500">
              Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
            </p>
            {qrUrl && (
              <div className="flex justify-center">
                <img src={qrUrl} alt="2FA QR Code" className="w-44 h-44 border border-slate-200 rounded-xl p-2" />
              </div>
            )}
            {secret && (
              <div className="bg-slate-50 rounded-lg px-3 py-2 text-center">
                <p className="text-xs text-slate-400 mb-1">Manual entry key</p>
                <code className="text-sm font-mono text-slate-700 tracking-widest">{secret}</code>
              </div>
            )}
            <div>
              <p className="text-sm font-medium text-slate-700 mb-2">Enter the 6-digit code from your app</p>
              <Input
                placeholder="000000"
                maxLength={6}
                value={otpValue}
                onChange={(e) => setOtpValue(e.target.value.replace(/\D/g, ""))}
                className="text-center text-xl tracking-[0.4em] font-mono h-12 rounded-xl"
                autoFocus
              />
            </div>
            <div className="flex gap-3">
              <Button className="flex-1" onClick={() => setEnableModalOpen(false)}>Cancel</Button>
              <Button
                type="primary"
                className="flex-1"
                loading={verifying}
                onClick={handleEnable}
              >
                Verify & Enable
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Disable 2FA Modal */}
      <Modal
        title="Disable Two-Factor Authentication"
        open={disableModalOpen}
        onCancel={() => setDisableModalOpen(false)}
        footer={null}
        width={380}
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-500">
            Enter your account password to disable 2FA. This will remove the extra security layer.
          </p>
          <Input.Password
            placeholder="Your password"
            value={disablePassword}
            onChange={(e) => setDisablePassword(e.target.value)}
            className="rounded-xl h-11"
            autoFocus
          />
          <div className="flex gap-3">
            <Button className="flex-1" onClick={() => setDisableModalOpen(false)}>Cancel</Button>
            <Button danger className="flex-1" loading={disabling} onClick={handleDisable}>
              Disable 2FA
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

// ─── Page ──────────────────────────────────────────────────────────────────────
const SettingsPage = () => {
  const navItems = [
    { key: "company",          label: "Company Profile",    icon: Building2    },
    { key: "financial",        label: "Financial & Formats", icon: DollarSign  },
    { key: "users",            label: "Users & Roles",      icon: Users        },
    { key: "billing",          label: "Billing & Plans",    icon: CreditCard   },
    { key: "security",         label: "Security",           icon: ShieldCheck  },
    { key: "notifications",    label: "Notifications",      icon: Bell         },
    { key: "integrations",     label: "Integrations",       icon: Plug         },
    { key: "jobtypes",         label: "Job Types & SLA",    icon: Briefcase    },
    { key: "branches",         label: "Branches",           icon: GitBranch    },
    { key: "divisions",        label: "Divisions",          icon: LayoutGrid   },
    { key: "holidays",         label: "Holidays",           icon: CalendarDays },
    { key: "feature_overrides",label: "Feature Overrides",  icon: ToggleLeft   },
    { key: "api",              label: "API & Webhooks",     icon: Key          },
    { key: "industry",         label: "Industry Config",    icon: Globe        },
  ];
  const [searchParams, setSearchParams] = useSearchParams();
  const active = searchParams.get("tab") ?? "company";

  const content: Record<string, React.ReactNode> = {
    company:          <CompanyTab />,
    financial:        <FinancialTab />,
    users:            <UsersTab />,
    billing:          <BillingTab />,
    security:         <SecurityTab />,
    notifications:    <NotificationsTab />,
    integrations:     <IntegrationsTab />,
    jobtypes:         <JobTypesTab />,
    branches:         <BranchesTab />,
    divisions:        <DivisionsTab />,
    holidays:         <HolidaysTab />,
    feature_overrides:<FeatureOverridesTab />,
    api:              <ApiTab />,
    industry:         <IndustryTab />,
  };

  return (
    <div className="p-6 min-h-screen bg-slate-50/50">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-slate-800">Settings</h1>
        <p className="text-slate-400 text-sm">Manage your account, team, billing and integrations</p>
      </div>
      <div className="flex gap-6 items-start">
        <div className="w-56 flex-shrink-0 bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = active === item.key;
            return (
              <button
                key={item.key}
                onClick={() => setSearchParams({ tab: item.key })}
                className={`w-full flex items-center justify-between px-4 py-3 text-sm transition-colors border-b border-slate-50 last:border-0 ${isActive ? "bg-blue-50 text-blue-700 font-semibold" : "text-slate-600 hover:bg-slate-50"}`}
              >
                <div className="flex items-center gap-2.5">
                  <Icon size={15} className={isActive ? "text-blue-600" : "text-slate-400"} />
                  {item.label}
                </div>
                <ChevronRight size={13} className={isActive ? "text-blue-400" : "text-slate-300"} />
              </button>
            );
          })}
        </div>
        <div className="flex-1 min-w-0">{content[active]}</div>
      </div>
    </div>
  );
};

export default SettingsPage;
